import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Container, MenuItem, Paper, TextField, Typography } from '@mui/material';
import AssignmentOutlined from '@mui/icons-material/AssignmentOutlined';
import { API_CONFIG } from '../../config/api';
import { NeurotrackProfile } from '../../services/neurotrackService';

const unavailable = 'Something went wrong. Please try again.';
const api = (path: string) => `${API_CONFIG.BASE_URL.replace(/\/$/, '')}/api/neurotrack/participant/${path}`;
const selector = () => {
  const values = new URLSearchParams(window.location.hash.slice(1)).getAll('invitation');
  return values.length === 1 && /^[A-Za-z0-9_-]{43}$/.test(values[0]) ? values[0] : null;
};
// The personal link travels in the request body, never in a URL or header.
async function send(path: string, body: object) {
  const response = await fetch(api(path), { method: 'POST', cache: 'no-store', referrerPolicy: 'no-referrer',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || unavailable);
  return data;
}
const today = () => new Date().toISOString().slice(0, 10);
const earliest = () => `${String(Number(today().slice(0, 4)) - 120).padStart(4, '0')}${today().slice(4)}`;
const validBirthDate = (value?: string): value is string => Boolean(value && /^\d{4}-\d{2}-\d{2}$/.test(value) &&
  Number.isFinite(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value && value >= earliest() && value <= today());
// Shown back in words so a mistyped year is easy to spot.
const spoken = (value: string) => new Date(`${value}T12:00:00`).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
// Only ever continue to Neurotrack's own HTTPS site.
const neurotrack = (value: unknown) => {
  try {
    const url = new URL(String(value));
    return url.protocol === 'https:' && /(^|\.)neurotrack\.(com|io)$/.test(url.hostname) ? url.href : null;
  } catch { return null; }
};

type Invitation = { label: string; studyName: string | null; requiredFields: (keyof NeurotrackProfile)[] };

export default function AssessmentInvitationStart({ leave = (url: string) => window.location.assign(url) }: { leave?: (url: string) => void }) {
  const [token] = useState(selector);
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [values, setValues] = useState<NeurotrackProfile>({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [revision, retry] = useState(0);
  const leaving = useRef(false);
  useEffect(() => { if (window.location.hash) window.history.replaceState(window.history.state, '', window.location.pathname); }, []);
  useEffect(() => {
    let current = true;
    if (!token) return;
    setError('');
    send('invitation', { invitationToken: token }).then(data => { if (current) setInvitation(data); })
      .catch(reason => { if (current) setError(reason.message); });
    return () => { current = false; };
  }, [token, revision]);

  const start = async () => {
    if (!invitation || busy || leaving.current) return;
    const fields = invitation.requiredFields;
    if (fields.includes('dateOfBirth') && !validBirthDate(values.dateOfBirth)) { setError('Enter a valid date of birth.'); return; }
    if (fields.includes('sex') && !values.sex) { setError('Select your gender.'); return; }
    setBusy(true); setError('');
    try {
      const profile = Object.fromEntries(fields.map(field => [field, values[field]]));
      const data = await send('start', { invitationToken: token, ...(fields.length ? { profile } : {}) });
      const url = neurotrack(data.url);
      if (url) { leaving.current = true; leave(url); return; }
      // Neurotrack turned out to be missing a detail this page had not asked for.
      if (Array.isArray(data.requiredFields) && data.requiredFields.length) {
        setInvitation({ ...invitation, requiredFields: data.requiredFields });
        setError('Please fill in the details below.');
      } else setError(unavailable);
    } catch (reason) { setError(reason instanceof Error ? reason.message : unavailable); }
    finally { if (!leaving.current) setBusy(false); }
  };

  const page = (content: React.ReactNode) => <Container maxWidth="sm"><Paper sx={{ my: 5, p: { xs: 3, sm: 4 } }}>
    <Box textAlign="center"><AssignmentOutlined sx={{ color: 'primary.main', fontSize: 48, mb: 2 }} />
      <Typography variant="h4" component="h1" gutterBottom>Your assessment</Typography></Box>
    {content}
  </Paper></Container>;
  if (!token) return page(<Alert severity="info">Please open the link from your invitation email.</Alert>);
  if (!invitation) return page(error
    ? <Alert severity="error" action={<Button color="inherit" onClick={() => retry(value => value + 1)}>Try again</Button>}>{error}</Alert>
    : <Box textAlign="center"><CircularProgress aria-label="Loading your assessment" /></Box>);
  const fields = invitation.requiredFields;
  return page(<Box component="form" noValidate onSubmit={(event: React.FormEvent) => { event.preventDefault(); void start(); }}>
    <Typography color="text.secondary" sx={{ mb: 2 }}>You are invited to take <strong>{invitation.label}</strong>
      {invitation.studyName ? ` for ${invitation.studyName}` : ''}.</Typography>
    {fields.length > 0 && <Alert severity="info" sx={{ mb: 1 }}>Enter your details to start. Please double-check them. They can&apos;t be changed later.</Alert>}
    {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
    {fields.includes('dateOfBirth') && <TextField fullWidth required type="date" label="Date of birth" margin="normal"
      value={values.dateOfBirth || ''} disabled={busy} InputLabelProps={{ shrink: true }} inputProps={{ min: earliest(), max: today() }}
      helperText={validBirthDate(values.dateOfBirth) ? spoken(values.dateOfBirth) : undefined}
      onChange={event => setValues(previous => ({ ...previous, dateOfBirth: event.target.value }))} />}
    {fields.includes('sex') && <TextField fullWidth required select label="Gender" margin="normal"
      value={values.sex || ''} disabled={busy}
      onChange={event => setValues(previous => ({ ...previous, sex: event.target.value as NeurotrackProfile['sex'] }))}>
      <MenuItem value="male">Male</MenuItem><MenuItem value="female">Female</MenuItem>
    </TextField>}
    <Button type="submit" fullWidth variant="contained" size="large" disabled={busy} sx={{ mt: 2 }}>{busy ? 'Starting…' : 'Start assessment'}</Button>
  </Box>);
}
